import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { UIProduct } from '../../types';

interface ProductCardProps {
  product: UIProduct;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid',
  borderColor: theme.palette.grey[200],
  backgroundColor: 'transparent',
  boxShadow: 'none',
  borderRadius: 12,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
    borderColor: theme.palette.grey[300],
    transform: 'translateY(-2px)',
  },
}));

const ProductIcon = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  marginBottom: theme.spacing(2),
  fontSize: '1.5rem',
  fontWeight: 500,
}));

const ProductCard: React.FC<ProductCardProps> = ({
  product,
}) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getColorFromName = (name: string) => {
    const colors = [
      '#4285f4',
      '#34a853',
      '#ea4335',
      '#fbbc04',
      '#673ab7',
      '#ff6d00',
      '#00acc1',
      '#8e24aa',
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  return (
    <StyledCard>
      <CardActionArea
        component={RouterLink}
        to={`/product/${product.id}`}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: 2,
        }}
      >
        <CardContent sx={{
          p: 0,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}>
          <ProductIcon sx={{ bgcolor: getColorFromName(product.name) }}>
            {getInitials(product.name)}
          </ProductIcon>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              mb: 1,
              fontSize: '1rem',
              lineHeight: 1.4,
              width: '100%',
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minHeight: '2.8em',
            }}
          >
            {product.name}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              width: '100%',
              textAlign: 'center',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexGrow: 1,
              minHeight: '4.5em',
            }}
          >
            {product.description}
          </Typography>
          {product.subscribable !== undefined && (
            <Box sx={{ mt: 1.5 }}>
              <Chip
                size="small"
                label={product.subscribable ? "Available to subscribe" : "Not subscribable"}
                color={product.subscribable ? "success" : "default"}
                variant={product.subscribable ? "filled" : "outlined"}
              />
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </StyledCard>
  );
};

export default ProductCard;
